import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { productionOrdersApi, type ProductionOrderRow } from "../../api/productionOrders";
import { ProductionOrderModal } from "./ProductionOrderModal";
import type { Order } from "../../api/orders";
import { useAuth } from "../../auth/AuthContext";
import { Spinner } from "../Loading";
import { formatDate, formatDateTime, formatNumber } from "../../lib/format";

/**
 * Narudžbenice koje vise o ovoj porudžbini.
 *
 * Bankom narudžbenicu pravi komercijalista i šalje je proizvodnji; ime obmanjuje, jer po njemu
 * deluje kao kupčev dokument. U starom ERP-u ta veza nigde ne stoji, pa se na pitanje „gde mi je
 * roba" nije moglo odgovoriti. Ovde nalog nastaje IZ porudžbine, pa veza dolazi sama.
 *
 * Pravi se samo za ono čega nema na stanju, pa pokriva podskup porudžbine: zato se pri otvaranju
 * biraju stavke i količine, a ne prepisuje se cela porudžbina.
 */
export function ProductionOrders({ order }: { order: Order }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [nalozi, setNalozi] = useState<ProductionOrderRow[] | null>(null);
  const [otvoren, setOtvoren] = useState(false);

  const sme = hasPermission("section:orders:write") && hasPermission("orders:transition");

  const ucitaj = useCallback(() => {
    productionOrdersApi.byOrder(order.id).then(setNalozi).catch(() => setNalozi([]));
  }, [order.id]);

  useEffect(ucitaj, [ucitaj]);

  /** How it came to be: by itself with the approval, by someone's click, or made in the ERP. */
  const nastanak = (n: ProductionOrderRow): string => {
    if (n.autoCreated) {
      return n.createdByName
        ? t("production.origin.autoBy", { name: n.createdByName, defaultValue: "automatski, pri odobrenju porudžbine (odobrio {{name}})" })
        : t("production.origin.auto", "automatski, pri odobrenju porudžbine");
    }
    if (n.createdByName) return t("production.origin.by", { name: n.createdByName, defaultValue: "kreirao {{name}}" });
    return n.fromErp ? t("production.origin.erp", "napravljena u ERP-u") : "";
  };

  // Dok nema nijedne i ne sme da je pravi, odeljak se ne crta: prazan red je šum, ne podatak.
  if (nalozi !== null && nalozi.length === 0 && !sme) return null;

  return (
    <>
    <ProductionOrderModal order={order} open={otvoren && sme} onClose={() => setOtvoren(false)} onCreated={ucitaj} />
    <section style={card}>
      <div style={zaglavlje}>
        <h2 style={naslov}>{t("production.section", "Narudžbenice (proizvodnja)")}</h2>
        {sme && (
          <button type="button" className="btn sm" onClick={() => setOtvoren(true)}>
            {t("production.new", "Nova narudžbenica")}
          </button>
        )}
      </div>

      {nalozi === null ? (
        <Spinner size={16} />
      ) : nalozi.length === 0 ? (
        <p style={tiho}>
          {t("production.none", "Za ovu porudžbinu još nije otvorena nijedna narudžbenica.")}
        </p>
      ) : (
        <table className="tbl">
          <thead>
            <tr>
              <th>{t("production.number", "Broj")}</th>
              <th>{t("production.col.status", "Status")}</th>
              <th style={{ textAlign: "right" }}>{t("production.quantity", "Količina")}</th>
              <th>{t("production.neededBy", "Treba do")}</th>
            </tr>
          </thead>
          <tbody>
            {nalozi.map((n) => (
              <tr key={n.id}>
                {/* The same document whether it came by itself or by a click, so it reads the same: number, when, how. */}
                <td>
                  <strong style={{ fontWeight: 600 }}>{n.orderNumber}</strong>
                  {/* When and how it came to be, under its number: as a column of its own it ran out of the card. */}
                  <div style={{ ...tiho, fontSize: 12 }}>{formatDateTime(n.createdAt)}</div>
                  {nastanak(n) && <div style={{ ...tiho, fontSize: 12 }}>{nastanak(n)}</div>}
                </td>
                <td><span style={oznakaStatusa(n.status)}>{statusTekst(n.status, t)}</span></td>
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {formatNumber(n.totalQuantity, undefined, { maximumFractionDigits: 0 })}
                </td>
                <td>{n.neededBy ? formatDate(n.neededBy) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

    </section>
    </>
  );
}

function statusTekst(s: string, t: (k: string, d: string) => string): string {
  switch (s) {
    case "NOVA": return t("production.status.new", "Nova");
    case "U_PROIZVODNJI": return t("production.status.inProduction", "U proizvodnji");
    case "ZAVRSENA": return t("production.status.done", "Završena");
    case "OTKAZANA": return t("production.status.cancelled", "Otkazana");
    default: return s;
  }
}

/**
 * Status se vidi i oblikom, ne samo tekstom.
 *
 * Reč je „status", ne „stanje": kod Bankoma „stanje" znači zalihe („komercijalista proverava
 * stanje skladišta"), pa bi ista reč za status dokumenta baš u ovom rečniku najviše zbunjivala.
 */
function oznakaStatusa(s: string): CSSProperties {
  const boje: Record<string, [string, string]> = {
    NOVA: ["var(--brand-primary)", "var(--brand-primary-50)"],
    U_PROIZVODNJI: ["var(--warn)", "color-mix(in srgb, var(--warn) 14%, transparent)"],
    ZAVRSENA: ["var(--ok)", "color-mix(in srgb, var(--ok) 14%, transparent)"],
    OTKAZANA: ["var(--ink-3)", "var(--surface-2)"],
  };
  const [fg, bg] = boje[s] ?? ["var(--ink-3)", "var(--surface-2)"];
  return {
    color: fg, background: bg, border: `1px solid ${fg}`,
    borderRadius: 999, padding: "1px 8px", fontSize: 11.5, fontWeight: 600,
  };
}

const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
  padding: 16, display: "flex", flexDirection: "column", gap: 12,
};
const zaglavlje: CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
};
const naslov: CSSProperties = { fontSize: 15, margin: 0 };
const tiho: CSSProperties = { color: "var(--ink-3)", fontSize: 13, margin: 0 };

